import { Injectable } from '@angular/core';
import moment from 'moment';
import { EmployeeCapacity } from '../../classes/models/employee/EmployeeCapacity';
import { Rota } from '../../classes/models/rota/Rota';
import { DailyRequiredEmployeePrediction } from '../../classes/models/prediction/DailyRequiredEmployeePrediction';
import { DailyFootfallPrediction } from '../../classes/models/prediction/DailyFootfallPrediction';
import { asDayOfWeek, DayOfWeek, dayOfWeekValues } from '../../classes/enums/DayOfWeek';

/**
 * This service aggregates predicted footfall and required employee data together with actual employee data from rota.
 */
@Injectable({
  providedIn: 'root'
})
export class EmployeeCapacityService {

  /**
   * Aggregates footfall and required minimum employees predictions by each working hour, and also finds how many actual employees has
   * been assigned for that hour. This data is grouped by prediction/rota date.
   * @param rota rota containing one week's data.
   * @param dailyFootfallPredictions footfall predictions containing one week's data.
   * @param dailyRequiredEmployeePredictions required minimum employee predictions containing one week's data.
   */
  findEmployeesCapacityByDate(
    rota: Rota,
    dailyFootfallPredictions: DailyFootfallPrediction[],
    dailyRequiredEmployeePredictions: DailyRequiredEmployeePrediction[]
  ): Map<DayOfWeek, EmployeeCapacity[]> {
    const employeesCapacityByDate = new Map<DayOfWeek, EmployeeCapacity[]>();
    dayOfWeekValues().forEach(dayOfWeek => employeesCapacityByDate.set(dayOfWeek, []));

    dailyRequiredEmployeePredictions.forEach(dailyEmployeePrediction => {
      const date = dailyEmployeePrediction.date;
      const dayOfWeek = asDayOfWeek(moment(date));
      const employeesCapacity: EmployeeCapacity[] = [];

      dailyEmployeePrediction.hourlyPredictions.forEach(hourlyEmployeePrediction => {
        const hour = moment(hourlyEmployeePrediction.hour, 'H').format('HH:mm:ss');
        const assignedEmployees = this.getAssignedEmployees(hour, dayOfWeek, rota);
        const footfall = this.getFootfall(dailyFootfallPredictions, date, hourlyEmployeePrediction.hour);
        employeesCapacity.push(new EmployeeCapacity(
          hour,
          footfall,
          hourlyEmployeePrediction.prediction,
          assignedEmployees));
      });

      employeesCapacityByDate.set(dayOfWeek, employeesCapacity);
    });
    return employeesCapacityByDate;
  }

  private getAssignedEmployees(hour: string, dayOfWeek: DayOfWeek, rota: Rota): number {
    const employeeIdsOfRotaAssignments = rota.rotaAssignments
      .filter(assignment => assignment.dayOfWeek === dayOfWeek)
      .filter(assignment => assignment.startTime <= hour && assignment.endTime > hour)
      .map(assignment => assignment.employeePositionId);
    const employeeIdsOfRoleAssignments = rota.roleAssignments
      .filter(assignment => assignment.dayOfWeek === dayOfWeek)
      .filter(assignment => assignment.startTime <= hour && assignment.endTime > hour)
      .map(assignment => assignment.employeePositionId);
    const ids = employeeIdsOfRotaAssignments.concat(employeeIdsOfRoleAssignments);
    return new Set(ids).size;
  }

  private getFootfall(dailyFootfallPredictions: DailyFootfallPrediction[], date: string, hour: number): number {
    const footfall = dailyFootfallPredictions
      .find(footfallDailyPrediction => footfallDailyPrediction.date === date)
      ?.hourlyPredictions
      .find(footfallHourlyPrediction => footfallHourlyPrediction.hour === hour)
      ?.prediction;
    return footfall ? footfall : 0;
  }

}
